import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatAthens } from "./social/SocialOverview";

// Social > Instagram (ig-publish): connect with Instagram Login, then compose, approve and
// publish to Gio's own professional account through Meta's official Instagram API.
// Nothing is published without an explicit "Approve" here. Every call is admin-only.

const NEON = { borderColor: "rgba(255, 16, 240, 0.28)", boxShadow: "0 0 26px -18px #FF10F0" };
const card = "rounded-xl border bg-black/60 p-5";
const btn = "rounded-lg border px-3 py-1.5 text-sm font-semibold transition disabled:opacity-40";
const pinkBtn = `${btn} border-[#FF10F0] bg-[#FF10F0]/15 text-pink-100 hover:bg-[#FF10F0]/25`;
const ghostBtn = `${btn} border-zinc-700 text-zinc-200 hover:border-pink-400`;
const CAPTION_MAX = 2200;
const HASHTAG_MAX = 30;
const KINDS = [
  { id: "IMAGE", label: "Photo" }, { id: "CAROUSEL", label: "Carousel (2–20)" },
  { id: "REELS", label: "Reel" }, { id: "STORIES", label: "Story" },
];
const SUBTABS = [
  { id: "compose", label: "Composer & Queue" }, { id: "history", label: "History" }, { id: "insights", label: "Insights" },
];
const STATUS_TONE = {
  draft: "text-zinc-300", approved: "text-emerald-300", scheduled: "text-sky-300",
  publishing: "text-amber-300", published: "text-pink-300", failed: "text-red-300",
};
const IG_ERRORS = {
  state_invalid: "The connect link was invalid or already used. Try again.",
  state_expired: "The connect link expired. Try again.",
  access_denied: "Instagram access was not granted.",
  encryption_not_configured: "The server has no FERNET_KEY, so the token could not be stored safely.",
  exchange_failed: "Instagram did not accept the login. Try again.",
  missing_permissions: "Both permissions are needed to publish. Try again and allow them.",
  missing_code: "Instagram did not return a login code. Try again.",
};

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => (typeof x === "string" ? x : x?.msg)).join(" ");
  return d || fallback;
};
export const countHashtags = (s) => (String(s || "").match(/(^|[^\w#])#[^\s#@]+/gu) || []).length;

function StatusCard({ st, onChanged }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/api/instagram/oauth/start");
      window.location.assign(data.authorize_url);
    } catch (e) {
      toast.error(errText(e, "Could not start the Instagram connection."));
      setBusy(false);
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? The stored token is deleted; queued posts stay as they are.")) return;
    setBusy(true);
    try {
      await api.post("/api/instagram/disconnect");
      toast.success("Instagram disconnected.");
      onChanged();
    } catch (e) {
      toast.error(errText(e, "Could not disconnect."));
    } finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/instagram/refresh-token");
      if (data.ok) toast.success("Access renewed.");
      else toast.message(data.outcome === "expired" ? "Access expired. Connect again." : "Nothing to renew right now.");
      onChanged();
    } catch (e) {
      toast.error(errText(e, "Token refresh failed."));
    } finally { setBusy(false); }
  };
  return (
    <section className={card} style={NEON} data-testid="ig-status-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-pink-300">Instagram account</p>
          {st.connected ? (
            <p className="mt-1 text-xl font-bold text-white" data-testid="ig-username">@{st.username || "connected"}</p>
          ) : (
            <p className="mt-1 text-lg font-semibold text-zinc-200" data-testid="ig-not-connected">Not connected</p>
          )}
          {st.connected && (
            <p className="mt-1 text-xs text-zinc-400" data-testid="ig-expiry">
              Access {st.state === "expired" ? "expired" : `valid until ${formatAthens(st.expires_at)}`}
              {st.days_left !== null && st.days_left !== undefined && st.state !== "expired" ? ` · ${st.days_left} days left` : ""}
              {st.state === "expiring" ? " · renews automatically" : ""}
            </p>
          )}
          {st.last_refresh_error && <p className="mt-1 text-xs text-amber-300">Last renewal failed: {st.last_refresh_error}</p>}
          <p className="mt-1 text-xs text-zinc-500" data-testid="ig-publishing-state">
            Publishing {st.publishing_enabled ? "is on" : "is paused (IG_PUBLISH_ENABLED is off)"} · {st.published_last_24h ?? 0}/{st.daily_cap ?? 25} posts in the last 24 h
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!st.connected || st.state === "expired" ? (
            <button type="button" className={pinkBtn} onClick={connect} disabled={busy || !st.encryption_configured || !st.app_configured}
              data-testid="ig-connect">Connect Instagram</button>
          ) : (
            <>
              <button type="button" className={ghostBtn} onClick={refresh} disabled={busy}>Renew access</button>
              <button type="button" className={ghostBtn} onClick={disconnect} disabled={busy} data-testid="ig-disconnect">Disconnect</button>
            </>
          )}
        </div>
      </div>
      {!st.encryption_configured && (
        <p role="alert" className="mt-3 text-xs text-red-300">The server has no FERNET_KEY, so Instagram cannot be connected yet (tokens are only stored encrypted).</p>
      )}
      {!st.app_configured && (
        <p role="alert" className="mt-2 text-xs text-red-300">INSTAGRAM_APP_ID / INSTAGRAM_APP_SECRET are not set on the server.</p>
      )}
      {st.legacy_plaintext_present && (
        <p className="mt-2 text-xs text-amber-300">An old pasted token is still stored unencrypted. It is not used for publishing; connect with the button above and ask for the encryption script to be run.</p>
      )}
    </section>
  );
}

function MediaPicker({ items, selected, onToggle, onFlag, multi }) {
  if (!items.length) return <p className="text-sm text-zinc-500">No media in the library yet.</p>;
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-6" data-testid="ig-media-grid">
      {items.map((m) => {
        const idx = selected.indexOf(m.id);
        return (
          <div key={m.id} className={`relative overflow-hidden rounded-lg border ${idx >= 0 ? "border-[#FF10F0]" : "border-zinc-800"}`}>
            <button type="button" disabled={!m.eligible} onClick={() => onToggle(m.id)}
              className="block aspect-square w-full bg-zinc-900 disabled:cursor-not-allowed" title={m.reasons.join(" ") || m.title}
              data-testid={`ig-media-${m.id}`}>
              {m.thumbnail_url
                ? <img src={m.thumbnail_url} alt={m.title || "Library item"} className={`h-full w-full object-cover ${m.eligible ? "" : "opacity-30"}`} />
                : <span className="text-[10px] text-zinc-500">{m.can_flag ? m.title || "No preview" : "Not allowed"}</span>}
            </button>
            {idx >= 0 && <span className="absolute left-1 top-1 rounded bg-[#FF10F0] px-1.5 text-[10px] font-bold text-black">{multi ? idx + 1 : "✓"}</span>}
            {m.media_type === "video" && <span className="absolute right-1 top-1 rounded bg-black/70 px-1 text-[10px] text-white">video</span>}
            {!m.eligible && m.can_flag && (
              <button type="button" onClick={() => onFlag(m.id)} className="absolute inset-x-1 bottom-1 rounded bg-black/80 px-1 py-0.5 text-[10px] text-pink-200">
                Mark safe for Instagram
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Composer({ media, onSaved, onFlag }) {
  const [kind, setKind] = useState("IMAGE");
  const [selected, setSelected] = useState([]);
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const multi = kind === "CAROUSEL";
  const tags = countHashtags(caption);
  const toggle = (id) => setSelected((cur) => {
    if (cur.includes(id)) return cur.filter((x) => x !== id);
    if (!multi) return [id];
    return cur.length >= 20 ? cur : [...cur, id];
  });
  const problems = [];
  if (!selected.length) problems.push("Pick media from the library.");
  if (multi && selected.length < 2) problems.push("A carousel needs at least 2 items.");
  if (caption.length > CAPTION_MAX) problems.push(`Caption is over ${CAPTION_MAX} characters.`);
  if (tags > HASHTAG_MAX) problems.push(`More than ${HASHTAG_MAX} hashtags.`);
  if (kind === "STORIES" && caption.trim()) problems.push("Stories do not take a caption.");
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/api/instagram/posts", {
        kind, items: selected.map((picture_id) => ({ picture_id })), caption, alt_text: alt, scheduled_at: when || null,
      });
      toast.success("Saved as a draft. Approve it in the queue to publish.");
      setSelected([]); setCaption(""); setAlt(""); setWhen("");
      onSaved();
    } catch (e) {
      toast.error(errText(e, "Could not save the draft."));
    } finally { setBusy(false); }
  };
  return (
    <section className={card} style={NEON} data-testid="ig-composer">
      <p className="text-xs uppercase tracking-widest text-pink-300">New post</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <button key={k.id} type="button" onClick={() => { setKind(k.id); setSelected((s) => (k.id === "CAROUSEL" ? s : s.slice(0, 1))); }}
            className={kind === k.id ? pinkBtn : ghostBtn} aria-pressed={kind === k.id}>{k.label}</button>
        ))}
      </div>
      <p className="mt-3 text-xs text-zinc-500">Only library items marked safe for Instagram can be picked. Members and VIP media are never allowed.</p>
      <div className="mt-2"><MediaPicker items={media} selected={selected} onToggle={toggle} onFlag={onFlag} multi={multi} /></div>
      <label className="mt-4 block text-xs text-zinc-400" htmlFor="ig-caption">Caption</label>
      <textarea id="ig-caption" data-testid="ig-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={4}
        disabled={kind === "STORIES"} placeholder={kind === "STORIES" ? "Stories have no caption" : "Write a caption…"}
        className="mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-950 p-2 text-sm text-white focus:border-[#FF10F0] focus:outline-none" />
      <p className="mt-1 text-xs text-zinc-500" data-testid="ig-caption-count">
        <span className={caption.length > CAPTION_MAX ? "text-red-300" : ""}>{caption.length}/{CAPTION_MAX}</span> characters ·{" "}
        <span className={tags > HASHTAG_MAX ? "text-red-300" : ""}>{tags}/{HASHTAG_MAX}</span> hashtags
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-zinc-400">Alt text (photos)
          <input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={1000}
            className="mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-950 p-2 text-sm text-white" />
        </label>
        <label className="text-xs text-zinc-400">Schedule (Athens time, optional)
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="ig-when"
            className="mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-950 p-2 text-sm text-white" />
        </label>
      </div>
      {problems.length > 0 && selected.length > 0 && <ul className="mt-3 list-disc pl-5 text-xs text-amber-300">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      <div className="mt-4 flex justify-end">
        <button type="button" className={pinkBtn} disabled={busy || problems.length > 0} onClick={save} data-testid="ig-save-draft">Save draft</button>
      </div>
    </section>
  );
}

function QueueRow({ post, media, connected, publishingOn, onChanged }) {
  const [busy, setBusy] = useState(false);
  const thumbs = post.items.map((i) => media.find((m) => m.id === i.picture_id)).filter(Boolean);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { toast.error(errText(e, "That did not work.")); onChanged(); }
    finally { setBusy(false); }
  };
  const approve = () => act(() => api.post(`/api/instagram/posts/${post.id}/approve`, {}),
    post.scheduled_at ? "Approved and scheduled." : "Approved. Publish it when you are ready.");
  const approveAndPublish = () => act(async () => {
    if (!["approved", "scheduled"].includes(post.status)) await api.post(`/api/instagram/posts/${post.id}/approve`, {});
    await api.post(`/api/instagram/posts/${post.id}/publish-now`);
  }, "Publishing to Instagram…");
  const unapprove = () => act(() => api.post(`/api/instagram/posts/${post.id}/unapprove`), "Back to draft.");
  const remove = () => { if (window.confirm("Delete this post from the queue?")) act(() => api.delete(`/api/instagram/posts/${post.id}`), "Deleted."); };
  const canApprove = ["draft", "failed"].includes(post.status);
  const canPublish = connected && publishingOn && ["draft", "failed", "approved", "scheduled"].includes(post.status);
  return (
    <li className="flex flex-wrap items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950/70 p-3" data-testid={`ig-post-${post.id}`}>
      <div className="flex -space-x-3">
        {thumbs.slice(0, 3).map((m) => (m.thumbnail_url
          ? <img key={m.id} src={m.thumbnail_url} alt="" className="h-12 w-12 rounded border border-black object-cover" />
          : <span key={m.id} className="h-12 w-12 rounded border border-black bg-zinc-800" />))}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs"><span className={`font-semibold uppercase ${STATUS_TONE[post.status] || ""}`} data-testid="ig-post-status">{post.status}</span>
          <span className="text-zinc-500"> · {post.kind}{post.items.length > 1 ? ` · ${post.items.length} items` : ""}</span></p>
        <p className="mt-1 line-clamp-2 text-sm text-zinc-200">{post.caption || <span className="text-zinc-500">No caption</span>}</p>
        <p className="mt-1 text-xs text-zinc-500">{post.scheduled_at ? `Scheduled ${formatAthens(post.scheduled_at)}` : "No time set"}
          {post.approved_by ? ` · approved by ${post.approved_by}` : ""}</p>
        {post.error && <p className="mt-1 text-xs text-red-300">{post.error}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {canApprove && <button type="button" className={ghostBtn} disabled={busy} onClick={approve} data-testid="ig-approve">{post.scheduled_at ? "Approve & schedule" : "Approve"}</button>}
        {canPublish && <button type="button" className={pinkBtn} disabled={busy} onClick={approveAndPublish} data-testid="ig-publish-now">Approve &amp; publish now</button>}
        {["approved", "scheduled"].includes(post.status) && <button type="button" className={ghostBtn} disabled={busy} onClick={unapprove}>Back to draft</button>}
        {post.status !== "publishing" && <button type="button" className={ghostBtn} disabled={busy} onClick={remove}>Delete</button>}
      </div>
    </li>
  );
}

export default function InstagramHub() {
  const [st, setSt] = useState(null);
  const [posts, setPosts] = useState([]);
  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState(null);
  const [sub, setSub] = useState("compose");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [s, p, m] = await Promise.all([
        api.get("/api/instagram/status"), api.get("/api/instagram/posts"), api.get("/api/instagram/media"),
      ]);
      setSt(s.data); setPosts(p.data.posts || []); setMedia(m.data.items || []);
    } catch (e) {
      setError(errText(e, "Could not load Instagram."));
    }
  }, []);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("ig_connected") === "1") toast.success("Instagram connected.");
    const err = params.get("ig_error");
    if (err) toast.error(IG_ERRORS[err] || "Instagram connection failed.");
  }, [load]);

  useEffect(() => {
    if (sub !== "insights" || !st?.connected) return;
    api.get("/api/instagram/stats").then((r) => setStats(r.data)).catch((e) => toast.error(errText(e, "Could not load insights.")));
  }, [sub, st?.connected]);

  const flag = async (id) => {
    if (!window.confirm("Mark this item as safe to post on Instagram (no nudity or explicit content)?")) return;
    try {
      await api.post(`/api/instagram/media/${id}/sfw`, { sfw_public: true });
      toast.success("Marked safe for Instagram.");
      load();
    } catch (e) { toast.error(errText(e, "This item cannot be used on Instagram.")); }
  };

  const queue = useMemo(() => posts.filter((p) => p.status !== "published"), [posts]);
  const history = useMemo(() => posts.filter((p) => p.status === "published")
    .sort((a, b) => String(b.published_at).localeCompare(String(a.published_at))), [posts]);

  if (error) {
    return <div role="alert" className="rounded-xl border border-red-900 bg-red-950/30 p-5 text-sm text-red-200">
      {error} <button type="button" onClick={load} className="ml-2 underline">Retry</button></div>;
  }
  if (!st) return <p className="text-sm text-zinc-400" data-testid="ig-loading">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="instagram-tab">
      <StatusCard st={st} onChanged={load} />
      <nav className="flex flex-wrap gap-2" aria-label="Instagram views">
        {SUBTABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setSub(t.id)} aria-pressed={sub === t.id}
            className={sub === t.id ? pinkBtn : ghostBtn} data-testid={`ig-sub-${t.id}`}>{t.label}</button>
        ))}
      </nav>
      {sub === "compose" && <>
        <Composer media={media} onSaved={load} onFlag={flag} />
        <section className={card} style={NEON} data-testid="ig-queue">
          <p className="text-xs uppercase tracking-widest text-pink-300">Queue</p>
          <p className="mt-1 text-xs text-zinc-500">Drafts are never published. Approve a post to schedule it, or approve and publish it now.</p>
          {queue.length === 0
            ? <p className="mt-3 text-sm text-zinc-500" data-testid="ig-queue-empty">The queue is empty.</p>
            : <ul className="mt-3 space-y-2">{queue.map((p) => (
              <QueueRow key={p.id} post={p} media={media} connected={st.connected} publishingOn={st.publishing_enabled} onChanged={load} />))}</ul>}
        </section>
      </>}
      {sub === "history" && (
        <section className={card} style={NEON} data-testid="ig-history">
          <p className="text-xs uppercase tracking-widest text-pink-300">Published</p>
          {history.length === 0 ? <p className="mt-3 text-sm text-zinc-500">Nothing published from GP App yet.</p> : (
            <ul className="mt-3 divide-y divide-zinc-800">{history.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0 truncate text-zinc-200">{p.kind} · {p.caption || "No caption"}</span>
                <span className="shrink-0 text-xs text-zinc-400">{formatAthens(p.published_at)}
                  {p.permalink && <> · <a href={p.permalink} target="_blank" rel="noopener noreferrer" className="text-pink-300 hover:text-pink-200">View ↗</a></>}</span>
              </li>))}</ul>)}
        </section>
      )}
      {sub === "insights" && (
        <section className={card} style={NEON} data-testid="ig-insights">
          {!st.connected ? <p className="text-sm text-zinc-500">Connect Instagram to see insights.</p> : !stats ? <p className="text-sm text-zinc-400">Loading…</p> : (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[["Followers", stats.followers], ["Posts", stats.media_count], ["Avg likes", stats.avg_likes], ["Engagement", `${stats.engagement_rate}%`]].map(([k, v]) => (
                <div key={k}><dt className="text-xs text-zinc-500">{k}</dt><dd className="text-2xl font-bold text-white">{v ?? "—"}</dd></div>))}
            </dl>)}
        </section>
      )}
    </div>
  );
}
